import { Button, Group, Textarea } from '@chakra-ui/react';
import React from 'react';
import { FiArrowUp } from 'react-icons/fi';

export interface ChatComposerRowProps {
  value: string;
  onChange: (next: string) => void;
  onSubmit: () => void | Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  /** When true (default), Enter submits; Shift+Enter inserts newline. */
  submitOnEnter?: boolean;
  /** Root element for focus management (e.g. query `textarea[data-chat-composer-text]`) */
  rootRef?: React.RefObject<HTMLDivElement | null>;
}

/**
 * Compact chat-style row: textarea + blue primary action (up arrow). Shared across dashboard,
 * document messages, AI audit modal, document prompt footer, and document AI chat.
 */
export function ChatComposerRow({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled = false,
  loading = false,
  submitOnEnter = true,
  rootRef,
}: ChatComposerRowProps) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!submitOnEnter) return;
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void onSubmit();
    }
  };

  return (
    <Group
      ref={rootRef}
      attached
      width="100%"
      minW={0}
      display="grid"
      gridTemplateColumns="1fr auto"
      alignItems="stretch"
    >
      <Textarea
        data-chat-composer-text="true"
        minW={0}
        w="100%"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={2}
        resize="none"
        minH="40px"
        maxH="120px"
        size="sm"
        fontSize="16px"
        disabled={disabled}
      />
      <Button
        type="button"
        size="sm"
        colorScheme="blue"
        variant="solid"
        onClick={() => void onSubmit()}
        disabled={!value.trim() || disabled}
        loading={loading}
        minW="44px"
        px={3}
        py={0}
        aria-label="Send"
        title="Send"
        lineHeight={1}
        style={{
          height: '100%',
          minHeight: '100%',
          alignSelf: 'stretch',
        }}
      >
        <FiArrowUp size={18} strokeWidth={2.5} aria-hidden />
      </Button>
    </Group>
  );
}
